import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CompanyFilter } from '../components/CompanyFilter';
import { useDateRange } from '../hooks/useDateRange';
import { DateRangeFilter } from '../components/DateRangeFilter';
import { AlertTriangle, CheckCircle, XCircle, Info } from 'lucide-react';

export default function DataQualityPage() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [sites, setSites] = useState([]);
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterSite, setFilterSite] = useState('all');
  const { dateFrom, dateTo, setDateFrom, setDateTo, dateParams } = useDateRange();

  const fetchData = () => {
    const params = {};
    if (filterCompany !== 'all') params.company_id = filterCompany;
    if (filterSite !== 'all') params.site_id = filterSite;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get(`/analytics/data-quality?${new URLSearchParams(params)}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get('/companies').then(r => setCompanies(r.data)).catch(() => {});
    api.get('/sites').then(r => setSites(r.data)).catch(() => {});
    fetchData();
  }, []);
  useEffect(() => { setLoading(true); fetchData(); }, [filterCompany, filterSite, dateFrom, dateTo]);

  const score = data.score || 0;
  const scoreColor = score >= 70 ? 'text-emerald-600' : score >= 40 ? 'text-amber-600' : 'text-red-600';
  const scoreBg = score >= 70 ? 'bg-emerald-500' : score >= 40 ? 'bg-amber-500' : 'bg-red-500';

  const metrics = [
    { label: 'Total Records', value: data.total_records || 0, icon: Info },
    { label: 'Completeness', value: `${(data.completeness_rate || 0).toFixed(0)}%`, icon: CheckCircle, color: (data.completeness_rate || 0) >= 70 ? 'text-emerald-600' : 'text-amber-600' },
    { label: 'Proof Compliance', value: `${(data.proof_compliance_rate || 0).toFixed(0)}%`, icon: CheckCircle, color: (data.proof_compliance_rate || 0) >= 70 ? 'text-emerald-600' : 'text-amber-600' },
    { label: 'Missing Product', value: data.missing_product || 0, icon: XCircle, color: (data.missing_product || 0) > 0 ? 'text-red-600' : 'text-emerald-600' },
    { label: 'Missing Price', value: data.missing_price || 0, icon: XCircle, color: (data.missing_price || 0) > 0 ? 'text-red-600' : 'text-emerald-600' },
    { label: 'Missing Hours', value: data.missing_hours || 0, icon: AlertTriangle, color: (data.missing_hours || 0) > 0 ? 'text-amber-600' : 'text-emerald-600' },
    { label: 'Missing Proof', value: data.missing_proof || 0, icon: AlertTriangle, color: (data.missing_proof || 0) > 0 ? 'text-amber-600' : 'text-emerald-600' },
    { label: 'Draft Records', value: data.draft_records || 0, icon: Info },
    { label: 'Missing BCM', value: data.missing_bcm || 0, icon: Info },
    { label: 'Missing Tonnage', value: data.missing_tonnage || 0, icon: Info },
  ];

  return (
    <div data-testid="data-quality-page">
      <div className="page-header">
        <div><h1 className="page-title">Data Quality</h1><p className="text-sm text-slate-500 mt-1">Monitor data completeness, proof compliance, and record reliability</p></div>
      </div>

      <div className="flex gap-3 mb-6 flex-wrap items-end" data-testid="quality-filters">
        <CompanyFilter companies={companies} value={filterCompany} onChange={setFilterCompany} />
        <Select value={filterSite} onValueChange={setFilterSite}><SelectTrigger className="w-48 bg-white"><SelectValue placeholder="All Sites" /></SelectTrigger><SelectContent><SelectItem value="all">All Sites</SelectItem>{sites.map(s => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}</SelectContent></Select>
        <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} prefix="quality" />
      </div>

      {loading ? <div className="animate-pulse h-48 bg-slate-100 rounded-lg" /> : (
        <>
          <Card className="bg-white border border-slate-200 rounded-lg p-6 mb-8" data-testid="quality-score-card">
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className={`text-5xl font-bold font-heading ${scoreColor}`}>{score}</p>
                <p className="text-xs text-slate-500 mt-1">out of 100</p>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-900 mb-2">Overall Data Quality Score</p>
                <div className="w-full bg-slate-100 rounded-full h-3">
                  <div className={`h-3 rounded-full ${scoreBg} transition-all duration-500`} style={{ width: `${score}%` }} />
                </div>
                <p className="text-xs text-slate-500 mt-2">Based on field completeness (50%), proof compliance (30%), and record approval status (20%)</p>
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
            {metrics.map(({ label, value, icon: Icon, color }) => (
              <Card key={label} className="bg-white border border-slate-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Icon size={14} className={color || 'text-slate-400'} />
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
                </div>
                <p className={`text-xl font-bold font-heading ${color || 'text-slate-900'}`}>{value}</p>
              </Card>
            ))}
          </div>

          <Card className="bg-white border border-slate-200 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-slate-900 font-heading mb-4">Issues & Alerts</h3>
            {(data.issues || []).length === 0 ? <p className="text-sm text-slate-400 text-center py-8">No issues detected</p> :
            <div className="space-y-3">
              {(data.issues || []).map((issue, i) => (
                <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${issue.severity === 'high' ? 'bg-red-50 border-red-100' : issue.severity === 'medium' ? 'bg-amber-50 border-amber-100' : 'bg-slate-50 border-slate-100'}`}>
                  <AlertTriangle size={16} className={`mt-0.5 flex-shrink-0 ${issue.severity === 'high' ? 'text-red-500' : issue.severity === 'medium' ? 'text-amber-500' : 'text-slate-400'}`} />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">{issue.message}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${issue.severity === 'high' ? 'bg-red-100 text-red-600' : issue.severity === 'medium' ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-500'}`}>{issue.severity}</span>
                      <span className="text-xs text-slate-400">{issue.count} records affected</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>}
          </Card>
        </>
      )}
    </div>
  );
}
